import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
    useForm,
    useWatch,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    CategoryInput,
    PourFormatInput,
    ProductInput,
    ProductLineInput,
    ProductSubtypeInput,
    ProductTypeInput,
    StandardDrinkInput,
} from '@barboxx/admin-components';
import { CREATE_POUR_AVAILABILITY_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PourAvailability } from '@barboxx/models';
import { POUR_AVAILABILITY_ENTITY_TYPE } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreatePourAvailabilityDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<PourAvailability.CreateRequest>({
        resolver: yupResolver(CREATE_POUR_AVAILABILITY_SCHEMA),
        defaultValues: {
            isDefault: false,
            entityType: undefined,
        },
    });

    const mutateCreatePourAvailability = useMutation({
        mutationKey: ['pourAvailability'],
        mutationFn: (data: PourAvailability.CreateRequest) =>
            Api.Menu.PourAvailability.postPourAvailability(data),
    });

    const onSubmit = async (values: PourAvailability.CreateRequest) => {
        return mutateCreatePourAvailability
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created !`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating pour availability!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    const values = useWatch(formContext) as PourAvailability.CreateRequest;

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Pour Availability
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <PourFormatInput />
                    <SelectElement
                        label="Entity Type"
                        name="entityType"
                        options={enumToFormOptions(
                            POUR_AVAILABILITY_ENTITY_TYPE
                        )}
                        fullWidth
                        onChange={() => {
                            formContext.resetField('productTypeId');
                            formContext.resetField('productSubtypeId');
                            formContext.resetField('categoryId');
                            formContext.resetField('productId');
                            formContext.resetField('productLineId');
                            formContext.resetField('standardDrinkId');
                        }}
                    />
                    {values.entityType ===
                        POUR_AVAILABILITY_ENTITY_TYPE.Drink && (
                        <StandardDrinkInput />
                    )}
                    {values.entityType ===
                        POUR_AVAILABILITY_ENTITY_TYPE.Product && (
                        <ProductInput />
                    )}
                    {values.entityType ===
                        POUR_AVAILABILITY_ENTITY_TYPE.ProductLine && (
                        <ProductLineInput />
                    )}
                    {values.entityType ===
                        POUR_AVAILABILITY_ENTITY_TYPE.ProductType && (
                        <ProductTypeInput />
                    )}
                    {values.entityType ===
                        POUR_AVAILABILITY_ENTITY_TYPE.ProductSubtype && (
                        <ProductSubtypeInput />
                    )}
                    {values.entityType ===
                        POUR_AVAILABILITY_ENTITY_TYPE.Category && (
                        <CategoryInput />
                    )}

                    <TextFieldElement
                        label="displayOrder"
                        name="displayOrder"
                        fullWidth
                    />

                    <CheckboxElement label="Default" name="isDefault" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
